import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"


type Workflow = {
  id: number
  name: string
  description: string | null
  enabled: boolean
  trigger_type: string
  trigger_label: string
  execution_mode: string
  execution_mode_label: string
  conditions_json: string
  actions_json: string
  conditions_summary: string
  action_summary: string
  scheduler_summary: string
  scheduler_warning: string
}

type Props = {
  workflow: Workflow
  workflow_errors: string[]
  flash_notice: string | null
}

export default function WorkflowsCanvas({ workflow, workflow_errors, flash_notice }: Props) {
  const conditions = parseJson(workflow.conditions_json, {}) as Record<string, unknown>
  const actions = parseJson(workflow.actions_json, []) as unknown[]
  const triggerChips = [workflow.trigger_label, workflow.execution_mode_label].filter(Boolean)
  const conditionChips = buildConditionChips(conditions)
  const actionBlocks = buildActionBlocks(actions)

  return (
    <>
      <Head title={`${workflow.name} Canvas | LeadFlow`}>
        <meta name="description" content={`Legacy canvas preview for ${workflow.name}.`} />
        <meta property="og:title" content={`${workflow.name} Canvas | LeadFlow`} />
        <meta property="og:description" content={`Legacy canvas preview for ${workflow.name}.`} />
      </Head>
      <AppShell wide>
        <PageHeader
          title={`${workflow.name} Canvas Preview`}
          description="Legacy canvas preview of the current workflow JSON. The JSON and form editor remain the source of truth."
          actions={<ButtonLink href={`/workflows/${workflow.id}`}>Open builder</ButtonLink>}
        />

        {flash_notice && <p className="mt-6 rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body">{flash_notice}</p>}
        {workflow_errors.length > 0 && <ErrorBox errors={workflow_errors} />}

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr_1fr]">
          <WorkflowBlock
            title="Trigger"
            description="What starts this workflow."
            chips={triggerChips}
            body={<>
              <p className="text-sm text-ink-body">Type: {workflow.trigger_type}</p>
              <p className="mt-1 text-sm text-ink-muted">{workflow.scheduler_summary}</p>
              <p className="mt-2 text-xs text-ink-muted">{workflow.scheduler_warning}</p>
            </>}
          />

          <WorkflowBlock
            title="Conditions"
            description="The matching rules currently stored on the workflow."
            chips={conditionChips}
            body={<>
              <p className="text-sm text-ink-body">{workflow.conditions_summary || "No conditions configured."}</p>
              <JsonPreview data={conditions} />
            </>}
          />

          <WorkflowBlock
            title="Actions"
            description="The actions that run when the trigger matches."
            chips={actionBlocks.map((item) => item.title)}
            body={<>
              <p className="text-sm text-ink-body">{workflow.action_summary || "No actions configured."}</p>
              <div className="mt-3 space-y-3">
                {actionBlocks.length === 0 ? (
                  <p className="text-sm text-ink-muted">No actions configured.</p>
                ) : (
                  actionBlocks.map((action, index) => (
                    <article key={`${action.title}-${index}`} className="rounded-md border border-hairline bg-page p-4 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-ink-display">{action.title}</h3>
                        {action.badge && <Badge tone={action.badge.tone}>{action.badge.label}</Badge>}
                      </div>
                      {action.detail && <p className="mt-1 text-ink-body">{action.detail}</p>}
                    </article>
                  ))
                )}
              </div>
            </>}
          />
        </div>
      </AppShell>
    </>
  )
}

function WorkflowBlock({ title, description, chips, body }: { title: string; description: string; chips: string[]; body: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-hairline bg-surface p-5">
      <h2>{title}</h2>
      <p className="mt-1 text-sm text-ink-muted">{description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {chips.filter(Boolean).map((chip) => <Badge key={chip} tone="muted">{chip}</Badge>)}
      </div>
      <div className="mt-4 rounded-md border border-hairline bg-page p-4">{body}</div>
    </section>
  )
}

function JsonPreview({ data }: { data: unknown }) {
  return <pre className="mt-3 overflow-x-auto rounded-md border border-hairline bg-surface p-3 text-xs text-ink-muted">{JSON.stringify(data, null, 2)}</pre>
}

function ErrorBox({ errors }: { errors: string[] }) {
  return <div className="mt-6 rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger-display"><p className="font-medium">Please fix the workflow form:</p><ul className="mt-2 list-disc space-y-1 pl-5">{errors.map((error) => <li key={error}>{error}</li>)}</ul></div>
}

function parseJson(value: string, fallback: unknown) {
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

function buildConditionChips(value: Record<string, unknown>) {
  const chips = [] as string[]
  if (Array.isArray(value.stage_keys) && value.stage_keys.length > 0) chips.push(`Stages: ${value.stage_keys.join(", ")}`)
  if (value.minimum_minutes) chips.push(`Minimum: ${value.minimum_minutes} minutes`)
  if (value.repeat_window_minutes) chips.push(`Repeat: ${value.repeat_window_minutes} minutes`)
  return chips
}

function buildActionBlocks(actions: unknown[]) {
  return actions.map((action: any) => {
    const type = String(action?.type || "unknown")
    if (type === "move_stage") {
      return { title: "Move stage", detail: `Target stage key: ${action?.to_stage_key || "unknown"}`, badge: { tone: "accent" as const, label: "Internal action" } }
    }
    if (type === "create_internal_note") {
      return { title: "Create internal note", detail: action?.body || "No note body configured.", badge: { tone: "neutral" as const, label: "Internal action" } }
    }
    if (type === "create_follow_up") {
      return { title: "Create follow-up", detail: action?.reason || "No follow-up reason configured.", badge: { tone: "neutral" as const, label: "Internal action" } }
    }
    if (type === "send_ctm_sms") {
      return { title: "SMS reserved for later", detail: "CTM/SMS is not active in this pass.", badge: { tone: "signal" as const, label: "Disabled" } }
    }
    return { title: humanize(type), detail: "Action shape is not recognized yet.", badge: { tone: "muted" as const, label: "Unknown" } }
  })
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function ButtonLink({ href, children }: { href: string; children: React.ReactNode }) {
  return <Link href={href} className="inline-flex items-center justify-center rounded-md border border-hairline bg-surface px-3 py-2 text-sm font-medium text-ink-body no-underline hover:bg-page">{children}</Link>
}
